'use client';

import {
  Avatar,
  AvatarFallback,
  AvatarImage,
} from '@ahafood/ui/components/avatar';
import { Badge } from '@ahafood/ui/components/badge';
import { Button } from '@ahafood/ui/components/button';
import {
  Card,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@ahafood/ui/components/card';
import { assetPrefix } from '@ahm/api-wrappers-core';
import { cn, formatCurrency } from '@ahm/common-helpers';
import { PlusIcon, TagIcon } from 'lucide-react';
import { useState, useMemo } from 'react';
import { EditItem } from './edit-item';
import type { Item } from '@/api/use-get-menu-order';
import type { CartStore } from '@/store/cart/cart-store';
import { useCartStore } from '@/store/cart/cart-store-provider';

type Props = {
  item: Item;
  onClick?: () => void;
};

const InactiveBadge = () => (
  <div className="absolute inset-0 bg-neutral-50/50 z-10 size-full rounded-md md:rounded-lg">
    <Badge className="absolute bg-neutral-90 hover:bg-white top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 z-20 px-3 py-1.5 shadow-md md:px-4 md:py-2 md:text-sm">
      <span className="font-bold text-nowrap hover:text-neutral-90">
        Đã ngừng bán
      </span>
    </Badge>
  </div>
);

type ActionButtonProps = {
  itemInCart: any;
  totalQuantity: number;
  onClick: (e: React.MouseEvent) => void;
};

const ActionButton = ({
  itemInCart,
  totalQuantity,
  onClick,
}: ActionButtonProps) => (
  <Button
    size="icon"
    variant={itemInCart ? 'outline' : 'link'}
    className={cn(
      'size-8 flex-none rounded-full absolute bottom-3 right-3 bg-primary-10 z-0 p-0 flex items-center justify-center text-center font-semibold md:size-10 md:bottom-4 md:right-4 md:hover:scale-110 md:transition-transform md:duration-200',
      {
        'text-neutral-90 bg-white border-primary-50 md:border-2': itemInCart,
      }
    )}
    onClick={onClick}
  >
    {itemInCart ? (
      <span className="md:text-base">{totalQuantity}</span>
    ) : (
      <PlusIcon className="size-5 md:size-6" />
    )}
  </Button>
);

export default function StoreItem({ item, onClick }: Props) {
  const itemsSelector = useMemo(() => (state: CartStore) => state.items, []);
  const cartItems = useCartStore(itemsSelector);

  const itemsInCart = useMemo(
    () =>
      Object.values(cartItems).filter(
        (cartItem) => Number(cartItem._id) === item._id
      ),
    [cartItems, item._id]
  );

  const totalQuantity = useMemo(
    () => itemsInCart.reduce((sum, cartItem) => sum + cartItem.quantity, 0),
    [itemsInCart]
  );

  const itemInCart = itemsInCart.length > 0 ? itemsInCart[0] : null;

  const [isEditOpen, setIsEditOpen] = useState(false);

  const isInactive = item.status === 'INACTIVE';

  const handleButtonClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (isInactive) return;

    if (itemInCart) {
      setIsEditOpen(true);
    } else {
      onClick?.();
    }
  };

  const imageSrc =
    item.image_thumb_url ?? `${assetPrefix}/static/images/empty-store-bot.webp`;

  return (
    <Card
      key={item._id}
      className={cn(
        'col-span-1 overflow-hidden shadow-none rounded-none flex flex-col gap-4 border-none md:border md:rounded-lg md:shadow-sm md:hover:shadow-md md:transition-shadow md:duration-200 md:p-4 md:gap-3'
      )}
    >
      <Avatar
        className={cn(
          'w-full h-auto aspect-square rounded-none flex-none relative overflow-hidden md:rounded-lg',
          {
            'cursor-default': isInactive,
            'cursor-pointer': !isInactive,
          }
        )}
        onClick={isInactive ? undefined : handleButtonClick}
      >
        {isInactive ? (
          <InactiveBadge />
        ) : (
          <ActionButton
            itemInCart={itemInCart}
            totalQuantity={totalQuantity}
            onClick={handleButtonClick}
          />
        )}

        <AvatarImage
          src={imageSrc}
          alt={item.name}
          className={cn('object-cover rounded-md lg:rounded-lg')}
        />
        <AvatarFallback className="rounded-none lg:rounded-lg">
          {item.name}
        </AvatarFallback>
      </Avatar>

      <CardHeader className="p-0 w-full space-y-1 lg:space-y-2">
        <CardTitle className="line-clamp-2 text-sm lg:text-base lg:font-semibold lg:leading-tight">
          {item.name}
        </CardTitle>
        <CardDescription className="flex flex-row items-start gap-1 text-xs font-medium lg:text-sm lg:flex-wrap">
          {item.discount_config ? (
            <>
              <TagIcon className="size-4 flex-none text-blue-50 lg:size-4" />
              <span className="text-blue-50">
                {item.discount_config.type === 'VALUE'
                  ? `-${formatCurrency(item.discount_config?.value)}`
                  : item.discount_config.type === 'PERCENT'
                    ? `${item.discount_config?.value}%`
                    : `Đồng giá ${formatCurrency(
                        item.discount_config?.value ?? 0
                      )}`}
              </span>
            </>
          ) : null}

          {item.discounted_price !== item.price ? (
            <span className="line-through lg:text-gray-500">
              {formatCurrency(item.price)}
            </span>
          ) : null}
        </CardDescription>
        <CardTitle className="line-clamp-2 text-lg lg:text-xl lg:font-bold lg:text-primary">
          {formatCurrency(item.discounted_price)}
        </CardTitle>
      </CardHeader>

      {itemInCart && (
        <EditItem
          item={item}
          isOpen={isEditOpen}
          onClose={() => setIsEditOpen(false)}
        />
      )}
    </Card>
  );
}
